import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductTypeForm from './ProductTypeForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import FieldHelp from '../../../shared/FieldHelp';
import { ActiveStatusPill, SystemPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

function ProductTypeTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/product-types', { params: { status, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Product Type "${item.code}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/product-types/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/product-types/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/product-types/${editItem.id}`, data);
      } else {
        await axios.post('/api/product-types', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Product Types</h2>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add Product Type
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <ProductTypeForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '21%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '9%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  Buy
                  <FieldHelp fieldKey="inventory-product-types.can-buy" pageKey="inventory-product-types" articleSlug="inventory-controls" articleTitle="Inventory Controls" label="Can Buy">Controls whether this Product/UoM appears on a Purchase Order.</FieldHelp>
                </span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  Sell
                  <FieldHelp fieldKey="inventory-product-types.can-sell" pageKey="inventory-product-types" articleSlug="inventory-controls" articleTitle="Inventory Controls" label="Can Sell">Controls whether this Product/UoM appears on a Pricelist, Quote, or Sales Order.</FieldHelp>
                </span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  Track
                  <FieldHelp fieldKey="inventory-product-types.can-track" pageKey="inventory-product-types" articleSlug="inventory-controls" articleTitle="Inventory Controls" label="Can Track">Controls whether on-hand quantity can be held directly for this Product/UoM.</FieldHelp>
                </span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.code}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.name}</td>
                <td
                  style={{ padding: '6px 8px', fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={item.description || ''}
                >
                  {item.description || '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>{item.can_buy ? '✓' : '–'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>{item.can_sell ? '✓' : '–'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>{item.can_track ? '✓' : '–'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.usage_count ? (
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                      title="See which Products use this Product Type"
                      onClick={() => navigate(`/products?product_type=${item.id}`)}
                    >
                      {item.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  {item.is_system_default ? <SystemPill /> : <ActiveStatusPill active={item.is_active} />}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? {
                            key: 'deactivate',
                            label: 'Deactivate',
                            danger: true,
                            disabled: showForm || item.is_system_default,
                            disabledTitle: showForm ? 'Finish editing first' : 'This is a locked Product Type and cannot be deactivated',
                            onClick: () => handleDeactivate(item),
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleReactivate(item),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Product Types"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ProductTypeTable;
